import type { CouponListItem } from '@tn/foundation/entities/coupon/types';
import { TrackEventTypes, TrackingCategory } from '@tn/foundation/entities/tracking/const';
import type { ProductResponse } from '@tn/foundation/graphql/__generated/gql/graphql';
import type { TrackingContextType } from '@tn/foundation/providers/TrackingProvider';
import { PAGE_TYPES, TRACK_LINK_NAMES } from '@tn/foundation/utils/constants/tracking';

import { devLog } from '../../utils/devLog';

import { getProductOfferPeriod } from './utils';

type TrackEvent = TrackingContextType['trackEvent'];

type CouponRedemptionSuccessParams = {
    /** Already translated label of the redeem CTA. */
    ctaText: string;
    coupon?: CouponListItem;
    userPlan: ProductResponse;
};

/**
 * Fires when the coupon is redeemed successfully.
 *
 * `legacyTrackAction`/`legacyTrackName` keep the old tracking library's eventName
 * (`${pathname}:${appName}:user_action_trackCancelOfferRedemptionSuccess`) unchanged
 * while the event also reaches DataHub.
 */
export const trackCouponRedemptionSuccess = (
    trackEvent: TrackEvent,
    { ctaText, coupon, userPlan }: CouponRedemptionSuccessParams,
): void => {
    try {
        trackEvent({
            type: TRACK_LINK_NAMES.TRACK_CANCEL_OFFER_REDEMPTION_SUCCESS,
            category: TrackingCategory.App.INTERACT,
            detail: {
                context: {
                    link_name: TRACK_LINK_NAMES.TRACK_CANCEL_OFFER_REDEMPTION_SUCCESS,
                    pageType: PAGE_TYPES.REDEEM_CANCEL_OFFER,
                    ctaText,
                    switchPlanClick: '1',
                    promoOffer: coupon?.normalizedCoupon?.code,
                    productDiscount: coupon?.normalizedCoupon?.couponString,
                    pickPlanType: userPlan.planTier,
                    pickPlanSku: userPlan.productCode,
                    purchaseProductName: userPlan.title,
                    productName: userPlan.title,
                    productOfferPeriod: getProductOfferPeriod(userPlan, coupon),
                    productPricingPlan: userPlan.planType,
                    purchasePrice: coupon?.normalizedCoupon?.reducedPrice,
                    productCurrentSku: userPlan.billingVendorProductCode,
                    productNewSku: userPlan.billingVendorProductCode,
                },
            },
            legacyTrackAction: TrackEventTypes.ACTION,
            legacyTrackName: TRACK_LINK_NAMES.TRACK_CANCEL_OFFER_REDEMPTION_SUCCESS,
        });
    } catch (e) {
        // Don't allow tracking failures to break user flow
        devLog('[trackCouponRedemptionSuccess] error', () => e);
    }
};

/** Matches the appLogType reported by the legacy call. */
const COUPON_REDEMPTION_FAIL = 'CouponRedemptionFail';

/**
 * Fires when the coupon redemption fails and the error banner is shown.
 *
 * Same legacy dual-support as {@link trackCouponRedemptionSuccess}.
 */
export const trackCouponRedemptionFailure = (
    trackEvent: TrackEvent,
    { errorMessage }: { errorMessage: string },
): void => {
    try {
        trackEvent({
            type: TRACK_LINK_NAMES.TRACK_APP_LOG,
            category: TrackingCategory.App.LOG,
            detail: {
                context: {
                    link_name: TRACK_LINK_NAMES.TRACK_APP_LOG,
                    pageType: PAGE_TYPES.REDEEM_CANCEL_OFFER,
                    appLogType: COUPON_REDEMPTION_FAIL,
                    appLogCode: 'No code provided',
                    appLogText: errorMessage,
                },
            },
            legacyTrackAction: TrackEventTypes.ACTION,
            legacyTrackName: TRACK_LINK_NAMES.TRACK_APP_LOG,
        });
    } catch (e) {
        // Don't allow tracking failures to break user flow
        devLog('[trackCouponRedemptionFailure] error', () => e);
    }
};
